import { FIREBASE_RUNTIME_BINDING } from "@/lib/firebaseRuntimeBinding";
import SEO_DATASET_MANIFEST from "@/public/seo-dataset-manifest.json";

export type GeneratedPublicSeoTarget = {
  projectId: string;
  frontendProjectId: string;
  storageBucket: string;
  functionsRegion: string;
};

export function assertGeneratedPublicSeoTarget(
  generatedTarget: GeneratedPublicSeoTarget
): void {
  if (
    generatedTarget.projectId !== FIREBASE_RUNTIME_BINDING.projectId ||
    generatedTarget.frontendProjectId !==
      FIREBASE_RUNTIME_BINDING.frontendProjectId ||
    generatedTarget.storageBucket !== FIREBASE_RUNTIME_BINDING.storageBucket ||
    generatedTarget.functionsRegion !== FIREBASE_RUNTIME_BINDING.functionsRegion
  ) {
    throw new Error("Generated public SEO target does not match this build.");
  }
}

export function assertGeneratedPublicSeoRoutes(
  kind: "businessProfileRoutes" | "searchLandingRoutes",
  routes: string[],
  generatedTarget: GeneratedPublicSeoTarget
): void {
  assertGeneratedPublicSeoTarget(generatedTarget);
  const manifestTarget = SEO_DATASET_MANIFEST.target;
  if (
    manifestTarget.projectId !== generatedTarget.projectId ||
    manifestTarget.frontendProjectId !== generatedTarget.frontendProjectId ||
    manifestTarget.storageBucket !== generatedTarget.storageBucket ||
    manifestTarget.functionsRegion !== generatedTarget.functionsRegion
  ) {
    throw new Error("Generated public SEO manifest target mismatch.");
  }

  const actual = [...routes].sort();
  const expected = [...SEO_DATASET_MANIFEST[kind]].sort();
  if (
    actual.length !== expected.length ||
    actual.some((route, index) => route !== expected[index])
  ) {
    throw new Error("Generated public SEO routes do not match the sitemap manifest.");
  }
}

export function bindGeneratedPublicSeoDataset<T extends readonly unknown[]>(
  kind: "businessProfileRoutes" | "searchLandingRoutes",
  records: T,
  sha256: string
): T {
  const manifestDigest =
    kind === "businessProfileRoutes"
      ? SEO_DATASET_MANIFEST.businessProfileDatasetSha256
      : SEO_DATASET_MANIFEST.searchLandingDatasetSha256;

  if (!/^[a-f0-9]{64}$/.test(sha256) || sha256 !== manifestDigest) {
    throw new Error("Generated public SEO dataset digest mismatch.");
  }

  return records;
}

export function bindGeneratedPublicStorageUrl(
  value: string | null | undefined,
  generatedTarget: GeneratedPublicSeoTarget
): string | null {
  assertGeneratedPublicSeoTarget(generatedTarget);
  const url = typeof value === "string" ? value.trim() : "";
  if (!url) return null;

  const match = url.match(
    /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\/o(?:\/|\?|$)/i
  );
  if (match && decodeURIComponent(match[1]) !== generatedTarget.storageBucket) {
    throw new Error("Generated public SEO storage URL target mismatch.");
  }

  return url;
}
